import { CalendarOutlined } from '@ant-design/icons';
import { Button } from '@components/Button/Button';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { DatePicker, Select, Space, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useGetOrganizationsShortQuery } from '@/api/organizations/api';
import {
  useLazyDownloadMortalityReportQuery,
  useLazyGetMortalityReportQuery,
} from '@/api/report/api';
import { mortalityColumns } from '@/pages/Report/columns/mortalityColumns';
import { extraButtonConfig } from '@/pages/Report/components/ReportTable/buttons.config';
import { MortalityReport, MortalityReportFilter } from '@/pages/Report/types';
import { Sort } from '@/shared/api/types';
import { useAppNotification } from '@/shared/hooks/useAppNotification';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { report } from '@/shared/security/permissions/report';
import { downloadBlob, mapTableIndex } from '@/utils/utils';

const { Title } = Typography;

const MORTALITY_REPORT_TITLE = 'Отчет по падежу';
const PERIOD_MONTH_FORMAT = 'YYYY-MM-DD';
const PERIOD_MONTH_DISPLAY_FORMAT = 'MM.YYYY';

// Отчет формируется только за завершенные месяцы.
const lastCompletedMonth = () => dayjs().subtract(1, 'month').startOf('month');

export const MortalityReportComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showWarning } = useAppNotification();

  const [periodMonth, setPeriodMonth] = useState<Dayjs | null>(() => {
    const periodMonthParam = searchParams.get('periodMonth');
    return periodMonthParam
      ? dayjs(periodMonthParam).startOf('month')
      : lastCompletedMonth();
  });
  const [organizationId, setOrganizationId] = useState<string | undefined>(
    () => searchParams.get('organizationId') || undefined,
  );
  const [currentSort, setCurrentSort] = useState<Sort[] | undefined>();

  const { data: organizations, isFetching: isOrganizationsFetching } =
    useGetOrganizationsShortQuery({ level: OrganizationLevelType.ORGANIZATION });
  const [getMortalityReport, mortalityReportResult] = useLazyGetMortalityReportQuery();
  const [downloadMortalityReport, downloadResult] = useLazyDownloadMortalityReportQuery();

  const organizationOptions = useMemo(
    () =>
      organizations?.map((organization) => ({
        label: organization.shortName || organization.name,
        value: organization.id,
      })) ?? [],
    [organizations],
  );

  const organizationName = useMemo(
    () =>
      organizationOptions.find((option) => option.value === organizationId)?.label ?? '',
    [organizationOptions, organizationId],
  );

  const filters = useMemo<MortalityReportFilter>(
    () => ({
      periodMonth: periodMonth?.format(PERIOD_MONTH_FORMAT) ?? '',
      ...(organizationId ? { organizationId } : {}),
    }),
    [periodMonth, organizationId],
  );

  const updateSearchParam = useCallback(
    (key: string, value?: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const resetReport = () => {
    setCurrentSort(undefined);
    mortalityReportResult.reset();
  };

  const handleChangePeriod = (value: Dayjs | null) => {
    const month = value ? value.startOf('month') : null;
    setPeriodMonth(month);
    updateSearchParam('periodMonth', month?.format(PERIOD_MONTH_FORMAT));
    resetReport();
  };

  const handleChangeOrganization = (value?: string) => {
    setOrganizationId(value);
    updateSearchParam('organizationId', value);
    resetReport();
  };

  const handleLoadReport = useCallback(
    (sort?: Sort[]) => {
      if (!periodMonth) return;

      setCurrentSort(sort);
      getMortalityReport({ filters, sort });
    },
    [periodMonth, filters, getMortalityReport],
  );

  const handleDownload = useCallback(async () => {
    if (!periodMonth) return;

    const result = await downloadMortalityReport(
      { filters, sort: currentSort },
      false,
    ).unwrap();

    if (result.meta.response?.status === 204) {
      showWarning('Внимание!', 'Отсутствуют данные для формирования файла');
      return;
    }

    const filename = [
      MORTALITY_REPORT_TITLE,
      organizationName,
      periodMonth.format(PERIOD_MONTH_DISPLAY_FORMAT),
    ]
      .filter(Boolean)
      .join(' ');
    downloadBlob(result.data, `${filename}.xlsx`);
  }, [periodMonth, filters, currentSort, organizationName, downloadMortalityReport]);

  const { data, isFetching, isSuccess } = mortalityReportResult;

  const tableData = useMemo(() => mapTableIndex<MortalityReport>(data ?? []), [data]);

  const title = [
    MORTALITY_REPORT_TITLE,
    organizationName,
    periodMonth ? `за ${periodMonth.format(PERIOD_MONTH_DISPLAY_FORMAT)}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <Title level={4}>Составить отчет за</Title>
      <Space wrap size="middle">
        <DatePicker
          picker="month"
          style={{ width: 256 }}
          format={PERIOD_MONTH_DISPLAY_FORMAT}
          placeholder="Выберите месяц"
          value={periodMonth}
          onChange={handleChangePeriod}
          disabledDate={(current: Dayjs) =>
            current && current.isAfter(lastCompletedMonth(), 'month')
          }
          suffixIcon={<CalendarOutlined style={{ color: '#022C43' }} />}
        />
        <Select
          style={{ width: 320 }}
          placeholder="Все хозяйства"
          allowClear
          showSearch
          optionFilterProp="label"
          loading={isOrganizationsFetching}
          options={organizationOptions}
          value={organizationId}
          onChange={handleChangeOrganization}
        />
      </Space>
      <div>
        <Button
          text="Сформировать отчет"
          type="primary"
          style={{ width: 200, marginTop: 15, marginBottom: 50 }}
          action={() => handleLoadReport()}
          loading={isFetching}
          disabled={!periodMonth}
        />
      </div>
      {periodMonth && isSuccess && (
        <>
          <PageTitle title={title} />
          <UTable
            size={'small'}
            data={tableData}
            columns={mortalityColumns}
            currentSort={currentSort}
            onChange={handleLoadReport}
            buttons={extraButtonConfig({
              onDownload: handleDownload,
              isDownloading: downloadResult.isFetching,
              security: { permissions: report.mortalityReport.export },
              disabled: !tableData.length,
            })}
          />
        </>
      )}
    </>
  );
};
